import { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Select,
  Checkbox,
  Button,
  Group,
  Alert,
  Text,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertTriangle, IconSend } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib";
import RichTextDiagnosis from "../pathologist-review/RichTextDiagnosis";
import {
  required,
  showValidationNotification,
} from "../../utils/formValidation";

interface ErrorReportModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  onSuccess?: () => void;
  suggestedErrorTypes?: string[];
}

interface ErrorType {
  id: string;
  error: string;
  description: string;
  category: string;
  blockCase: boolean;
  preventSignOff: boolean;
  isActive: boolean;
}

// Map database error codes to human-readable display names
const ERROR_DISPLAY_NAMES: Record<string, string> = {
  SpecNotRcvd: "Specimen Not Received",
  PatData: "Patient Data Missing",
  NoSpec: "No Specimen in Container",
};

function getErrorDisplayName(errorCode: string): string {
  return ERROR_DISPLAY_NAMES[errorCode] || errorCode;
}

export default function ErrorReportModal({
  opened,
  onClose,
  caseId,
  onSuccess,
  suggestedErrorTypes = [],
}: ErrorReportModalProps) {
  const queryClient = useQueryClient();
  const [blockCase, setBlockCase] = useState(false);
  const [preventSignOff, setPreventSignOff] = useState(false);

  const form = useForm({
    initialValues: {
      errorId: "",
      description: "",
    },
    validate: {
      errorId: required("Please select an error type"),
      description: (value: string) => {
        const plain = value.replace(/<[^>]*>/g, "").trim();
        if (!plain) return "Please provide a detailed description";
        if (plain.length < 10)
          return "Description must be at least 10 characters";
        return null;
      },
    },
  });

  // Fetch active errors from database
  const { data: errors = [], isLoading: loadingErrors } = useQuery({
    queryKey: ["errors"],
    queryFn: async () => {
      return api.get<ErrorType[]>("/admin/errors");
    },
  });

  // Create error report mutation
  const createMutation = useMutation({
    mutationFn: async (data: {
      caseId: string;
      errorId: string;
      description: string;
      blockCase: boolean;
      preventSignOff: boolean;
    }) => {
      return api.post("/workflows/errors", data);
    },
    onSuccess: () => {
      // Invalidate error queries
      queryClient.invalidateQueries({ queryKey: ["error-reports"] });
      queryClient.invalidateQueries({ queryKey: ["caseErrors", caseId] });
      // Invalidate case queries to update blocked status
      queryClient.invalidateQueries({
        queryKey: ["case-for-grossing", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-embedding", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-cutting", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({ queryKey: ["grossing-queue"] });
      queryClient.invalidateQueries({ queryKey: ["embedding-queue"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-page"] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
    },
  });

  // Filter to only show active errors
  const activeErrors = errors.filter((e: ErrorType) => e.isActive);

  // Find suggested errors based on missing fields
  const suggestedErrors = activeErrors.filter((e: ErrorType) =>
    suggestedErrorTypes.includes(e.error)
  );

  // Auto-select the first suggested error when modal opens or errors load
  useEffect(() => {
    if (opened && suggestedErrors.length > 0 && !form.values.errorId) {
      form.setFieldValue("errorId", suggestedErrors[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, errors.length, suggestedErrorTypes]);

  // Reset form when modal closes
  useEffect(() => {
    if (!opened) {
      form.reset();
      setBlockCase(false);
      setPreventSignOff(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Get selected error details
  const selectedError = activeErrors.find(
    (e: ErrorType) => e.id === form.values.errorId
  );

  // Use error's default flags as initial state (user can override)
  useEffect(() => {
    if (selectedError) {
      setBlockCase(selectedError.blockCase || false);
      setPreventSignOff(selectedError.preventSignOff || false);
    } else {
      setBlockCase(false);
      setPreventSignOff(false);
    }
  }, [selectedError]);

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      await createMutation.mutateAsync({
        caseId,
        errorId: values.errorId,
        description: values.description,
        blockCase,
        preventSignOff,
      });

      notifications.show({
        title: "Error Reported Successfully",
        message: blockCase
          ? "Error reported. Case has been blocked until resolved."
          : "Error reported successfully",
        color: "green",
      });

      form.reset();
      setBlockCase(false);
      setPreventSignOff(false);

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error: any) {
      showValidationNotification(error.message || "Failed to report error");
    }
  });

  const handleCancel = () => {
    form.reset();
    setBlockCase(false);
    setPreventSignOff(false);
    onClose();
  };

  // Determine button text and color
  const getButtonText = () => {
    if (blockCase) return "Report & Block Case";
    if (preventSignOff) return "Report Error";
    return "Report Error";
  };

  const getButtonColor = () => {
    if (blockCase) return "red";
    return "blue";
  };

  // Build select data - suggested errors first, then others
  const otherErrors = activeErrors.filter(
    (e: ErrorType) => !suggestedErrorTypes.includes(e.error)
  );

  // Combine suggested first, then others - flat list with isRecommended flag
  const errorSelectData = [
    ...suggestedErrors.map((e: ErrorType) => ({
      value: e.id,
      label: getErrorDisplayName(e.error),
      description: e.description,
      isRecommended: true,
    })),
    ...otherErrors.map((e: ErrorType) => ({
      value: e.id,
      label: getErrorDisplayName(e.error),
      description: e.description,
      isRecommended: false,
    })),
  ];

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={
        <Group gap="xs">
          <IconAlertTriangle size={20} />
          <Text fw={600}>Report Error</Text>
        </Group>
      }
      size="lg"
      closeOnClickOutside={false}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {loadingErrors ? (
            <Loader size="sm" />
          ) : (
            <>
              <Select
                label="Error Type"
                placeholder="Select an error type"
                data={errorSelectData}
                value={form.values.errorId}
                onChange={(value) => form.setFieldValue("errorId", value || "")}
                error={form.errors.errorId}
                withAsterisk
                searchable
                renderOption={({ option }) => {
                  const errorOption = option as {
                    label: string;
                    value: string;
                    description?: string;
                    isRecommended?: boolean;
                  };
                  return (
                    <div>
                      <Text size="sm" fw={500}>
                        {errorOption.label}
                      </Text>
                      {errorOption.isRecommended && (
                        <Text size="xs" fs="italic" c="blue">
                          Recommended
                        </Text>
                      )}
                      {errorOption.description && (
                        <Text size="xs" c="dimmed">
                          {errorOption.description}
                        </Text>
                      )}
                    </div>
                  );
                }}
              />

              {selectedError && selectedError.description && (
                <Alert color="blue" variant="light">
                  {/* eslint-disable react/no-danger */}
                  <div
                    style={{
                      fontSize: "var(--mantine-font-size-sm)",
                      wordBreak: "break-word",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: selectedError.description,
                    }}
                  />
                  {/* eslint-enable react/no-danger */}
                </Alert>
              )}

              <RichTextDiagnosis
                label="Detailed Description *"
                placeholder="Provide specific details about the error..."
                value={form.values.description}
                onChange={(val) => form.setFieldValue("description", val)}
                minHeight={120}
                error={form.errors.description as string | undefined}
              />
              <Text size="xs" c="dimmed" mt={-8}>
                Describe the specific issue encountered (minimum 10 characters)
              </Text>

              <Stack gap="xs">
                <Checkbox
                  label="Block case from progressing"
                  description="Prevents the case from moving forward until resolved"
                  checked={blockCase}
                  onChange={(e) => setBlockCase(e.target.checked)}
                />

                <Checkbox
                  label="Prevent pathologist sign-off"
                  description="Requires resolution before final sign-off"
                  checked={preventSignOff}
                  onChange={(e) => setPreventSignOff(e.target.checked)}
                />
              </Stack>

              {blockCase && (
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  title="Case will be blocked"
                  color="orange"
                >
                  This case will be blocked from progressing through the
                  workflow until the error is resolved by the accessioning team.
                </Alert>
              )}
            </>
          )}
        </Stack>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            color={getButtonColor()}
            leftSection={<IconSend size={16} />}
            loading={createMutation.isPending}
          >
            {getButtonText()}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
